import React, { useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { usePermissions } from '../../../hooks/usePermissions';
import { getSalesId, getSalesValue, useSalesApiList } from '../hooks/useSalesApiResource';

const normalizeRule = (rule = {}) => ({
  ...rule,
  id: Number(getSalesId(rule) || 0),
  workflowName: String(getSalesValue(rule, 'workflowName', 'WorkflowName') || ''),
  ruleJson: String(getSalesValue(rule, 'ruleJson', 'RuleJson') || ''),
  isActive: Boolean(getSalesValue(rule, 'isActive', 'IsActive') ?? true),
});

const normalizeRules = (rows) => rows.map(normalizeRule);

const RulesList = () => {
  const { canEdit } = usePermissions();
  const canEditSales = canEdit('sales.container');
  const [searchTerm, setSearchTerm] = useState('');

  const { items: rules, loading, error } = useSalesApiList((endpoints) => endpoints.rules.base, {
    normalize: normalizeRules,
    errorMessage: 'Unable to load rules.',
  });

  const filteredRules = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return rules;
    return rules.filter((rule) =>
      [rule.workflowName, rule.ruleJson].some((value) => String(value || '').toLowerCase().includes(needle))
    );
  }, [rules, searchTerm]);

  const truncate = (text, length) => (text.length > length ? `${text.slice(0, length)}..` : text);

  return (
    <section className="sales-portal-page" aria-labelledby="sales-rules-title">
      <Row className="mb-3">
        <Col><h4 id="sales-rules-title">Rules List</h4></Col>
      </Row>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <div className="position-relative search-input">
              <i className="bi bi-search position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#626262' }}></i>
              <Form.Control type="text" placeholder="Search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} style={{ paddingLeft: '40px' }} />
            </div>
          </Form.Group>
        </Col>
        <Col md={6} className="text-end">
          <Button className="btn-success addbtnview" disabled={!canEditSales}>
            <i className="bi bi-plus-circle me-2"></i>
            Add New Rule
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr className="rule-table">
                  <th style={{ width: '28%' }}>Workflow Name</th>
                  <th style={{ width: '57%' }}>Expression</th>
                  <th style={{ width: '8%' }}>Active</th>
                  <th style={{ width: '7%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4}><div className="sales-no-data"><Spinner animation="border" size="sm" /> Loading rules...</div></td></tr>
                ) : filteredRules.length === 0 ? (
                  <tr><td colSpan={4}><div className="sales-no-data">No rules found.</div></td></tr>
                ) : (
                  filteredRules.map((rule) => (
                    <tr key={rule.id} className="rule-table">
                      <td>{rule.workflowName}</td>
                      <td title={rule.ruleJson}>{truncate(rule.ruleJson, 120)}</td>
                      <td><Form.Check type="checkbox" checked={rule.isActive} disabled readOnly /></td>
                      <td className="text-center">
                        <button type="button" className="edit-icon" title="Edit" disabled={!canEditSales}>
                          <i className="bi bi-pencil-square"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RulesList;
